import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import {
  createMcpApiKey,
  createMcpEndpoint,
  deleteMcpEndpoint,
  getMcpEndpoints,
  getMcpSettings,
  getMyMcpApiKeys,
  getOrganizationMcpApiKeys,
  revokeMcpApiKey,
  rotateMcpApiKey,
  setMcpEnabled,
} from '@/apis/admin-api';

const getErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as { response?: { data?: unknown } }).response?.data;
  return typeof data === 'string' && data ? data : fallback;
};

export const useGetMyMcpApiKeys = () => useQuery({ queryKey: ['mcp-api-keys', 'mine'], queryFn: getMyMcpApiKeys });

export const useGetOrganizationMcpApiKeys = (enabled: boolean) =>
  useQuery({ queryKey: ['mcp-api-keys', 'organization'], queryFn: getOrganizationMcpApiKeys, enabled });

export const useCreateMcpApiKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMcpApiKey,
    // The response holds the one-time secret: don't keep it in the mutation cache.
    gcTime: 0,
    // Don't return the refetch: the dialog showing the secret must not wait for it.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mcp-api-keys'] });
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to create MCP API key')),
  });
};

export const useRotateMcpApiKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: rotateMcpApiKey,
    // The response holds the new one-time secret: don't keep it in the mutation cache.
    gcTime: 0,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mcp-api-keys'] });
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to rotate MCP API key')),
  });
};

export const useRevokeMcpApiKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: revokeMcpApiKey,
    onSuccess: () => {
      toast.success('MCP API key revoked');
      return queryClient.invalidateQueries({ queryKey: ['mcp-api-keys'] });
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to revoke MCP API key')),
  });
};

export const useGetMcpEndpoints = () => useQuery({ queryKey: ['mcp-endpoints'], queryFn: getMcpEndpoints });

export const useCreateMcpEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMcpEndpoint,
    onSuccess: () => {
      toast.success('MCP server created');
      return queryClient.invalidateQueries({ queryKey: ['mcp-endpoints'] });
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to create MCP server')),
  });
};

export const useDeleteMcpEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMcpEndpoint,
    onSuccess: () => {
      toast.success('MCP server deleted');
      // Deleting a server revokes its keys.
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['mcp-endpoints'] }),
        queryClient.invalidateQueries({ queryKey: ['mcp-api-keys'] }),
      ]);
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to delete MCP server')),
  });
};

export const useGetMcpSettings = () => useQuery({ queryKey: ['mcp-settings'], queryFn: getMcpSettings });

export const useSetMcpEnabled = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setMcpEnabled,
    // Fail instead of pausing while offline, so the Turn off dialog never waits forever.
    networkMode: 'always',
    onSuccess: async (settings) => {
      toast.success(
        settings.mcpEnabled ? 'MCP turned on for your organization' : 'MCP turned off for your organization'
      );
      await queryClient.cancelQueries({ queryKey: ['mcp-settings'] });
      queryClient.setQueryData(['mcp-settings'], settings);
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Failed to change the MCP setting')),
  });
};
